import React, { useMemo, useState } from 'react';
import {
  PlusIcon,
  BellIcon,
  CheckIcon,
  MessageCircleIcon,
  XIcon,
  PencilIcon,
  RepeatIcon,
  FileTextIcon,
  Calendar,
  Clock,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Toast } from '../Toast';
import ActionCard from '../common/ActionCard';
import {
  useClientReminders,
  useInvalidateReminders,
  ReminderRecord,
} from '../../hooks/useReminders';
import { useAuth } from '../../contexts/AuthContext';
import { apiRequest } from '../../lib/api';
import { useMutation } from '@tanstack/react-query';
import { useRecurringTemplates } from '../../hooks/useRecurringTemplates';
import { useCreateRecurringTemplate, useDeleteRecurringTemplate, useUpdateRecurringTemplate } from '../../hooks/useRecurringTemplateMutations';
import { RecurringReminderForm, RecurringReminderFormData } from '../RecurringReminderForm';
import { RecurringIndicator } from '../RecurringIndicator';
import { formatRecurrencePattern, formatEndCondition, RecurringTemplateWithClient } from '../../types/recurring-reminders';

interface RemindersTabProps {
  clientId: string;
  onNavigateToChat?: () => void;
}

type ReminderFilter = 'pending' | 'done' | 'cancelled' | 'recurring' | 'all';

type ReminderFormState = {
  title: string;
  description: string;
  date: string;
  time: string;
};

const initialFormState: ReminderFormState = {
  title: '',
  description: '',
  date: '',
  time: '',
};

function toDateInputValue(value?: string) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  // Format as YYYY-MM-DD in local timezone
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function toTimeInputValue(value?: string) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  // Format as HH:MM in local timezone
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
}

function combineDateTime(date: string, time: string) {
  if (!date || !time) return null;
  const iso = new Date(`${date}T${time}`);
  if (Number.isNaN(iso.getTime())) return null;
  return iso.toISOString();
}

function formatDue(iso: string) {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return { dateLabel: iso, timeLabel: '' };
  }
  return {
    dateLabel: date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }),
    timeLabel: date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    }),
  };
}

export function RemindersTab({
  clientId,
  onNavigateToChat,
}: RemindersTabProps) {
  const { session } = useAuth();
  const navigate = useNavigate();
  const remindersQuery = useClientReminders(clientId);
  const invalidateReminders = useInvalidateReminders(clientId);

  const handleNavigateToPlan = (planId: string) => {
    navigate(`/client/${clientId}?tab=plans&planId=${planId}`);
  };

  // Recurring reminders hooks
  const recurringTemplatesQuery = useRecurringTemplates(clientId);
  const createRecurringTemplate = useCreateRecurringTemplate(clientId);
  const deleteRecurringTemplate = useDeleteRecurringTemplate(clientId);

  const [showForm, setShowForm] = useState(false);
  const [editingRecurringTemplate, setEditingRecurringTemplate] =
    useState<RecurringTemplateWithClient | null>(null);
  const updateRecurringTemplate = useUpdateRecurringTemplate(
    clientId,
    editingRecurringTemplate?.id || ''
  );
  const [showRecurringForm, setShowRecurringForm] = useState(false);
  const [reminderType, setReminderType] = useState<'one-time' | 'recurring'>('one-time');
  const [activeFilter, setActiveFilter] =
    useState<ReminderFilter>('pending');
  const [formState, setFormState] =
    useState<ReminderFormState>(initialFormState);
  const [formError, setFormError] = useState<string | null>(null);
  const [editingReminder, setEditingReminder] =
    useState<ReminderRecord | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [cancelContext, setCancelContext] = useState<{
    reminder: ReminderRecord | null;
    reason: string;
    error: string | null;
  }>({ reminder: null, reason: '', error: null });

  const reminders = remindersQuery.data ?? [];
  const recurringTemplates = recurringTemplatesQuery.data ?? [];

  const stats = useMemo(
    () =>
      reminders.reduce(
        (acc, reminder) => {
          acc.total += 1;
          acc[reminder.status] += 1;
          return acc;
        },
        {
          total: 0,
          pending: 0,
          done: 0,
          cancelled: 0,
          recurring: recurringTemplates.length,
        } as Record<'total' | 'pending' | 'done' | 'cancelled' | 'recurring', number>,
      ),
    [reminders, recurringTemplates.length],
  );

  const filteredReminders = useMemo(() => {
    if (activeFilter === 'recurring') return [];
    if (activeFilter === 'all') return reminders;
    return reminders.filter((reminder) => reminder.status === activeFilter);
  }, [reminders, activeFilter]);

  const reminderMutation = useMutation({
    mutationFn: async ({
      reminderId,
      payload,
    }: {
      reminderId?: string | null;
      payload: { title: string; description?: string | null; due_at: string };
    }) => {
      if (!session?.access_token) {
        throw new Error('Session expired. Please log in again.');
      }
      const method = reminderId ? 'PATCH' : 'POST';
      const url = reminderId
        ? `/api/clients/${clientId}/reminders/${reminderId}`
        : `/api/clients/${clientId}/reminders`;
      return apiRequest<ReminderRecord>(
        url,
        {
          method,
          body: JSON.stringify(payload),
        },
        session.access_token,
      );
    },
    onSuccess: (data, variables) => {
      setShowForm(false);
      setEditingReminder(null);
      setFormState(initialFormState);
      setFormError(null);
      setToastMessage(
        variables.reminderId ? 'Reminder updated' : 'Reminder saved',
      );
      invalidateReminders();
    },
    onError: (error) => {
      setFormError(
        error instanceof Error ? error.message : 'Failed to save reminder',
      );
    },
  });

  const statusMutation = useMutation({
    mutationFn: async ({
      reminderId,
      updates,
    }: {
      reminderId: string;
      updates: Record<string, unknown>;
    }) => {
      if (!session?.access_token) {
        throw new Error('Session expired. Please log in again.');
      }
      return apiRequest<ReminderRecord>(
        `/api/clients/${clientId}/reminders/${reminderId}`,
        {
          method: 'PATCH',
          body: JSON.stringify(updates),
        },
        session.access_token,
      );
    },
    onSuccess: (data, variables) => {
      setToastMessage('Reminder updated');
      invalidateReminders();
      setCancelContext({ reminder: null, reason: '', error: null });
    },
    onError: (error) => {
      const message =
        error instanceof Error ? error.message : 'Failed to update reminder';
      setCancelContext((prev) =>
        prev.reminder ? { ...prev, error: message } : prev,
      );
      if (!cancelContext.reminder) {
        setToastMessage(message);
      }
    },
  });

  const handleOpenForm = (reminder?: ReminderRecord, type: 'one-time' | 'recurring' = 'one-time') => {
    if (reminder) {
      setEditingReminder(reminder);
      setFormState({
        title: reminder.title,
        description: reminder.description ?? '',
        date: toDateInputValue(reminder.due_at),
        time: toTimeInputValue(reminder.due_at),
      });
      setReminderType('one-time'); // Editing is always one-time
    } else {
      setEditingReminder(null);
      setFormState(initialFormState);
      setReminderType(type);
    }
    setFormError(null);
    if (type === 'recurring') {
      setShowRecurringForm(true);
    } else {
      setShowForm(true);
    }
  };

  const handleSaveReminder = async () => {
    if (!formState.title.trim()) {
      setFormError('Title is required');
      return;
    }
    const dueAt = combineDateTime(formState.date, formState.time);
    if (!dueAt) {
      setFormError('Select a valid date and time');
      return;
    }
    await reminderMutation.mutateAsync({
      reminderId: editingReminder?.id,
      payload: {
        title: formState.title.trim(),
        description: formState.description.trim() || null,
        due_at: dueAt,
      },
    });
  };

  const handleMarkDone = async (reminder: ReminderRecord) => {
    await statusMutation.mutateAsync({
      reminderId: reminder.id,
      updates: { status: 'done' },
    });
  };

  const handleReopen = async (reminder: ReminderRecord) => {
    await statusMutation.mutateAsync({
      reminderId: reminder.id,
      updates: { status: 'pending' },
    });
  };

  const openCancelModal = (reminder: ReminderRecord) => {
    setCancelContext({ reminder, reason: '', error: null });
  };

  const handleConfirmCancel = async () => {
    if (!cancelContext.reminder) return;
    if (!cancelContext.reason.trim()) {
      setCancelContext((prev) => ({
        ...prev,
        error: 'Provide a cancellation reason',
      }));
      return;
    }
    await statusMutation.mutateAsync({
      reminderId: cancelContext.reminder.id,
      updates: {
        status: 'cancelled',
        cancel_reason: cancelContext.reason.trim(),
      },
    });
  };

  const handleOpenEditRecurringForm = (template: RecurringTemplateWithClient) => {
    setEditingRecurringTemplate(template);
    setShowRecurringForm(true);
  };

  const handleCloseRecurringForm = () => {
    setShowRecurringForm(false);
    setEditingRecurringTemplate(null);
  };

  const handleSubmitRecurringTemplate = async (data: RecurringReminderFormData) => {
    if (!editingRecurringTemplate) {
      // Create mode
      try {
        await createRecurringTemplate.mutateAsync(data);
        handleCloseRecurringForm();
        setToastMessage('Recurring reminder created');
      } catch (error) {
        setToastMessage('Failed to create recurring reminder');
      }
    } else {
      // Edit mode
      try {
        await updateRecurringTemplate.mutateAsync(data);
        handleCloseRecurringForm();
        setToastMessage('Recurring reminder updated');
      } catch (error) {
        setToastMessage('Failed to update recurring reminder');
      }
    }
  };

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex flex-wrap gap-2">
          {(['pending', 'done', 'cancelled', 'recurring', 'all'] as ReminderFilter[]).map(
            (filter) => (
              <button
                key={filter}
                onClick={() => setActiveFilter(filter)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeFilter === filter
                    ? 'bg-[#5B8E87] text-white'
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                {filter === 'pending'
                  ? `Upcoming (${stats.pending})`
                  : filter === 'done'
                  ? `Done (${stats.done})`
                  : filter === 'cancelled'
                  ? `Cancelled (${stats.cancelled})`
                  : filter === 'recurring'
                  ? `Recurring (${stats.recurring})`
                  : `All (${stats.total})`}
              </button>
            ),
          )}
        </div>
      </div>

      {activeFilter === 'recurring' ? (
        recurringTemplatesQuery.isLoading ? (
          <div className="bg-white rounded-2xl shadow-sm p-6 text-center text-gray-500 border border-gray-100">
            Loading recurring reminders...
          </div>
        ) : recurringTemplates.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm p-8 text-center">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <RepeatIcon className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-[#212121] mb-2">
              No recurring reminders yet
            </h3>
            <p className="text-gray-600 mb-4">
              Create repeating reminders for regular check-ins or follow-ups.
            </p>
            <button
              onClick={() => {
                setEditingRecurringTemplate(null);
                setShowRecurringForm(true);
              }}
              className="bg-[#5B8E87] text-white px-6 py-2 rounded-lg font-medium"
            >
              Add Recurring Reminder
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {recurringTemplates.map((template) => {
              const nextOccurrence = template.next_occurrence
                ? new Date(template.next_occurrence)
                : null;
              return (
                <div
                  key={template.id}
                  className="bg-white rounded-2xl shadow-sm p-4 border border-blue-100"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <RepeatIcon className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-[#212121]">{template.title}</h3>
                        <RecurringIndicator size="sm" />
                      </div>
                      {template.description && (
                        <p className="text-sm text-gray-600 mb-2">{template.description}</p>
                      )}
                      <div className="text-xs text-gray-500 space-y-1">
                        <p className="font-medium">{formatRecurrencePattern(template)}</p>
                        <p>{formatEndCondition(template)}</p>
                        {nextOccurrence && (
                          <p className="text-blue-600">
                            Next: {nextOccurrence.toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit',
                            })}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={() => handleOpenEditRecurringForm(template)}
                      className="flex-1 py-2 px-3 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors font-medium text-sm"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => {
                        if (confirm(`Delete recurring reminder "${template.title}"?`)) {
                          deleteRecurringTemplate.mutate(template.id, {
                            onSuccess: () => setToastMessage('Recurring reminder deleted'),
                            onError: () => setToastMessage('Failed to delete recurring reminder'),
                          });
                        }
                      }}
                      className="flex-1 py-2 px-3 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors font-medium text-sm"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )
      ) : remindersQuery.isLoading ? (
        <div className="bg-white rounded-2xl shadow-sm p-6 text-center text-gray-500 border border-gray-100">
          Loading reminders...
        </div>
      ) : remindersQuery.isError ? (
        <div className="bg-white rounded-2xl shadow-sm p-6 text-center text-red-500 border border-red-200">
          Unable to load reminders right now.
        </div>
      ) : filteredReminders.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm p-8 text-center">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-4xl">🔔</span>
          </div>
          <h3 className="text-lg font-semibold text-[#212121] mb-2">
            Nothing in this view
          </h3>
          <p className="text-gray-600 mb-4">
            Set follow-up reminders to keep momentum going.
          </p>
          <button
            onClick={() => handleOpenForm()}
            className="bg-[#5B8E87] text-white px-6 py-2 rounded-lg font-medium"
          >
            Add reminder
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredReminders.map((reminder) => {
            const due = formatDue(reminder.due_at);
            const isPending = reminder.status === 'pending';
            const isDone = reminder.status === 'done';
            const isCancelled = reminder.status === 'cancelled';

            // Icon colors based on state
            const iconBgColor = isDone
              ? 'bg-gray-100'
              : isCancelled
              ? 'bg-red-100'
              : 'bg-[#C9A870]/10';
            const iconColor = isDone
              ? 'text-gray-400'
              : isCancelled
              ? 'text-red-600'
              : 'text-[#C9A870]';

            // Card state for border
            const cardState = isDone
              ? 'completed'
              : isCancelled
              ? 'cancelled'
              : 'default';

            // Build title with badges
            const titleContent = (
              <div>
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="font-semibold text-sm text-[#212121]">
                    {reminder.title}
                  </span>
                  {reminder.plan_id && reminder.plan_title && (
                    <button
                      onClick={() => handleNavigateToPlan(reminder.plan_id!)}
                      className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#5B8E87] bg-opacity-10 rounded-full hover:bg-opacity-20 transition-colors"
                    >
                      <FileTextIcon className="w-3 h-3 text-[#5B8E87]" />
                      <span className="text-xs text-[#5B8E87] font-medium">
                        {reminder.plan_title}
                      </span>
                    </button>
                  )}
                  {reminder.is_recurring_instance && (
                    <RecurringIndicator size="sm" />
                  )}
                </div>
                {reminder.description && (
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {reminder.description}
                  </p>
                )}
                {isCancelled && reminder.cancel_reason && (
                  <p className="text-xs text-red-500 mt-1">
                    Cancelled: {reminder.cancel_reason}
                  </p>
                )}
              </div>
            );

            // Metadata
            const metadata = [
              { icon: <Calendar className="w-3.5 h-3.5" />, text: due.dateLabel },
              { icon: <Clock className="w-3.5 h-3.5" />, text: due.timeLabel },
            ];

            // Status badge for non-pending reminders
            const statusBadge = !isPending ? (
              <span
                className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                  isDone
                    ? 'bg-gray-100 text-gray-600'
                    : 'bg-red-50 text-red-600'
                }`}
              >
                {isDone ? 'Completed' : 'Cancelled'}
              </span>
            ) : null;

            // Actions based on state
            let primaryAction;
            let secondaryActions = [];
            let menuItems = [];

            if (isPending) {
              // Pending reminders
              primaryAction = {
                label: 'Mark Done',
                icon: <CheckIcon className="w-4 h-4" />,
                onClick: () => handleMarkDone(reminder),
                variant: 'success' as const,
              };

              if (onNavigateToChat) {
                secondaryActions.push({
                  icon: <MessageCircleIcon className="w-4 h-4" />,
                  onClick: onNavigateToChat,
                  ariaLabel: 'Open Chat',
                  variant: 'default' as const,
                });
              }

              menuItems = [
                {
                  label: 'Edit',
                  icon: <PencilIcon className="w-4 h-4" />,
                  onClick: () => handleOpenForm(reminder),
                },
                {
                  label: 'Cancel',
                  icon: <XIcon className="w-4 h-4" />,
                  onClick: () => openCancelModal(reminder),
                  variant: 'danger' as const,
                },
              ];
            } else {
              // Done or Cancelled reminders
              primaryAction = {
                label: 'Reopen',
                icon: <RepeatIcon className="w-4 h-4" />,
                onClick: () => handleReopen(reminder),
                variant: 'primary' as const,
              };
            }

            return (
              <ActionCard
                key={reminder.id}
                icon={<BellIcon className="w-5 h-5" />}
                iconBgColor={iconBgColor}
                iconColor={iconColor}
                title={titleContent}
                metadata={metadata}
                showClientInfo={false}
                statusBadge={statusBadge}
                cardState={cardState}
                primaryAction={primaryAction}
                secondaryActions={secondaryActions}
                menuItems={menuItems}
                actionsLayout="inline"
              />
            );
          })}
        </div>
      )}

      <button
        onClick={() => handleOpenForm()}
        className="fixed bottom-24 right-6 bg-[#C9A870] text-white w-14 h-14 rounded-full shadow-lg flex items-center justify-center hover:bg-[#B89660] transition-all active:scale-95 z-20"
      >
        <PlusIcon className="w-6 h-6" />
      </button>

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-md overflow-hidden animate-scale-in">
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-semibold text-[#212121]">
                  {editingReminder ? 'Edit Reminder' : 'Add Reminder'}
                </h2>
                <button
                  onClick={() => {
                    setShowForm(false);
                    setFormState(initialFormState);
                    setEditingReminder(null);
                    setFormError(null);
                  }}
                  className="text-gray-500"
                >
                  <XIcon className="w-6 h-6" />
                </button>
              </div>
              {!editingReminder && (
                <div className="flex gap-2">
                  <button
                    onClick={() => setReminderType('one-time')}
                    className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                      reminderType === 'one-time'
                        ? 'bg-[#5B8E87] text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    One-time
                  </button>
                  <button
                    onClick={() => {
                      setShowForm(false);
                      setEditingRecurringTemplate(null);
                      setShowRecurringForm(true);
                      setReminderType('recurring');
                    }}
                    className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-1 ${
                      reminderType === 'recurring'
                        ? 'bg-[#5B8E87] text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <RepeatIcon className="w-4 h-4" />
                    Recurring
                  </button>
                </div>
              )}
            </div>
            <div className="p-4 space-y-4">
              {formError && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2">
                  {formError}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-[#212121] mb-2">
                  Title
                </label>
                <input
                  type="text"
                  value={formState.title}
                  onChange={(e) =>
                    setFormState((prev) => ({
                      ...prev,
                      title: e.target.value,
                    }))
                  }
                  placeholder="e.g., Weekly progress check"
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#212121] mb-2">
                  Description
                </label>
                <textarea
                  value={formState.description}
                  onChange={(e) =>
                    setFormState((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  rows={3}
                  placeholder="Add context or agenda for this reminder"
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-[#212121] mb-2">
                    Date
                  </label>
                  <input
                    type="date"
                    value={formState.date}
                    onChange={(e) =>
                      setFormState((prev) => ({
                        ...prev,
                        date: e.target.value,
                      }))
                    }
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#212121] mb-2">
                    Time
                  </label>
                  <input
                    type="time"
                    value={formState.time}
                    onChange={(e) =>
                      setFormState((prev) => ({
                        ...prev,
                        time: e.target.value,
                      }))
                    }
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all"
                  />
                </div>
              </div>
            </div>
            <div className="p-4 bg-gray-50 border-t border-gray-200 flex gap-3">
              <button
                onClick={() => {
                  setShowForm(false);
                  setFormState(initialFormState);
                  setEditingReminder(null);
                  setFormError(null);
                }}
                className="flex-1 bg-gray-100 text-gray-600 font-semibold py-3 rounded-xl hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveReminder}
                disabled={reminderMutation.isPending}
                className={`flex-1 font-semibold py-3 rounded-xl ${
                  reminderMutation.isPending
                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    : 'bg-[#5B8E87] text-white'
                }`}
              >
                {reminderMutation.isPending ? 'Saving...' : 'Save Reminder'}
              </button>
            </div>
          </div>
        </div>
      )}

      {cancelContext.reminder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-md overflow-hidden animate-scale-in">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-[#212121]">
                Cancel Reminder
              </h2>
              <button
                onClick={() =>
                  setCancelContext({ reminder: null, reason: '', error: null })
                }
                className="text-gray-500"
              >
                <XIcon className="w-6 h-6" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <p className="text-sm text-gray-600">
                Provide a reason so it shows up on the timeline.
              </p>
              {cancelContext.error && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2">
                  {cancelContext.error}
                </div>
              )}
              <textarea
                value={cancelContext.reason}
                onChange={(e) =>
                  setCancelContext((prev) => ({
                    ...prev,
                    reason: e.target.value,
                    error: null,
                  }))
                }
                rows={3}
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all"
                placeholder="e.g., Client rescheduled to next week"
              />
            </div>
            <div className="p-4 bg-gray-50 border-t border-gray-200 flex gap-3">
              <button
                onClick={() =>
                  setCancelContext({ reminder: null, reason: '', error: null })
                }
                className="flex-1 bg-gray-100 text-gray-600 font-semibold py-3 rounded-xl hover:bg-gray-200 transition-colors"
              >
                Back
              </button>
              <button
                onClick={handleConfirmCancel}
                disabled={statusMutation.isPending}
                className={`flex-1 font-semibold py-3 rounded-xl ${
                  statusMutation.isPending
                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    : 'bg-red-500 text-white'
                }`}
              >
                {statusMutation.isPending ? 'Cancelling...' : 'Cancel Reminder'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showRecurringForm && (
        <RecurringReminderForm
          onClose={handleCloseRecurringForm}
          onSubmit={handleSubmitRecurringTemplate}
          initialData={editingRecurringTemplate || undefined}
          isLoading={
            editingRecurringTemplate
              ? updateRecurringTemplate.isPending
              : createRecurringTemplate.isPending
          }
          error={
            editingRecurringTemplate
              ? updateRecurringTemplate.error?.message
              : createRecurringTemplate.error?.message
          }
        />
      )}

      {toastMessage && (
        <Toast message={toastMessage} onClose={() => setToastMessage(null)} />
      )}
    </div>
  );
}
